import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '@/components/ui/daisy';
import { useModalDialog } from '@/components/ui/daisy/modal/hooks';
import { ListCacheManager } from '@/data/hook/query/v2/CacheFacade';
import useDeferredUnmount from '@/data/hook/shared/useDeferredUnmount';
import useSafePortal from '@/data/hook/useSafePortal';
import { Assessment, AssessmentItem } from '@/models/Assessment';
import Evaluation from '@/models/Evaluation';
import AssessmentItemFormService from './AssessmentItemFormService';
import ConditionalRenderer from '../ui/custom/ConditionalRenderer';

const EXIT_ANIMATION_MS = 200;

type AssessmentItemFormModalProps = {
  evaluation: Evaluation;
  assessment: Assessment;
  cacheManager: ListCacheManager<Evaluation>;
  index?: number;
  items: AssessmentItem[];
  onClose: () => void;
  onSuccess?: () => void;
};

export default function AssessmentItemFormModal({
  evaluation,
  assessment,
  cacheManager,
  index,
  items,
  onClose,
  onSuccess,
}: AssessmentItemFormModalProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.evaluationsTab',
  });

  const createPortal = useSafePortal();

  const visible = index !== undefined;
  const item = index !== undefined ? items[index] : undefined;
  const isCreate = visible && !item;

  const lastOpenStateRef = useRef({ item, isCreate });
  if (visible) {
    lastOpenStateRef.current = { item, isCreate };
  }

  const shouldRenderContent = useDeferredUnmount(visible, EXIT_ANIMATION_MS);

  const { ref, close } = useModalDialog(visible, onClose);

  const renderedItem = lastOpenStateRef.current.item;
  const renderedIsCreate = lastOpenStateRef.current.isCreate;

  useEffect(() => {
    if (!visible) ref.current?.close();
  }, [visible, ref]);

  return createPortal(
    <Modal ref={ref} className="modal-middle">
      <Modal.Box className="flex flex-col gap-4 max-w-xl">
        <h3 className="text-lg font-semibold text-base-content">
          {renderedIsCreate
            ? t('newAssessmentItemFormTitle')
            : t('assessmentItemFormTitle')}
        </h3>
        <ConditionalRenderer condition={shouldRenderContent}>
          <AssessmentItemFormService
            evaluation={evaluation}
            assessment={assessment}
            cacheManager={cacheManager}
            item={renderedItem}
            onClose={close}
            onSuccess={onSuccess}
          />
        </ConditionalRenderer>
        <Modal.Close onClick={close} />
      </Modal.Box>
      <Modal.Backdrop>
        <button onClick={close} type="button">
          close
        </button>
      </Modal.Backdrop>
    </Modal>,
  );
}
